import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminAuthService from '../../services/adminAuthService';
import { getRoleDisplayName } from '../../utils/adminPermissions';
import AdminSidebar from '../dashboard/AdminSidebar';
import AdminNotificationsDropdown from './AdminNotificationsDropdown';
import LanguageSwitcher from './LanguageSwitcher';
import './AdminLayout.css';

// Maps a URL segment to the same key used in the sidebar nav, so a
// breadcrumb never needs its own separate translation.
function routeLabel(t, segment) {
  const map = {
    dashboard: 'nav.dashboard', users: 'nav.users', stores: 'nav.stores',
    products: 'nav.products', orders: 'nav.orders', drivers: 'nav.drivers',
    requests: 'nav.deliveryRequests', carts: 'nav.carts', payments: 'nav.payments',
    messages: 'nav.messages', analytics: 'nav.analytics', 'admin-users': 'nav.adminUsers',
    activity: 'nav.activityLog', promotions: 'nav.promotions',
    'delivery-settings': 'nav.deliverySettings', 'delivery-ratings': 'nav.ratings',
    settings: 'nav.settings', 'app-version': 'nav.appVersions', payouts: 'nav.payouts',
    finance: 'nav.finance', escrow: 'nav.escrow', 'blocked-payouts': 'nav.blockedPayouts',
    'delivery-claims': 'nav.deliveryClaims',
  };
  const key = map[segment];
  return key ? t(key) : segment.charAt(0).toUpperCase() + segment.slice(1);
}

const getInitials = (email = '') => {
  if (!email) return 'A';
  return email.charAt(0).toUpperCase();
};

const AdminLayout = () => {
  const { t } = useTranslation();
  const [user, setUser]           = useState(null);
  const [adminRole, setAdminRole] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const u    = await AdminAuthService.getCurrentUser();
        const role = await AdminAuthService.getAdminRole();
        setUser(u);
        setAdminRole(role);
      } catch {
        navigate('/login', { state: { redirectTo: location.pathname } });
      }
    };
    load();
  }, [navigate, location.pathname]);

  const handleLogout = async () => {
    setLoading(true);
    try {
      await AdminAuthService.deleteAllSessions();
      localStorage.removeItem('adminUserId');
      navigate('/login', { replace: true });
    } catch (err) {
      console.error(err);
      navigate('/login', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  // Build breadcrumbs from URL segments
  const segments = location.pathname.split('/').filter(Boolean);
  const currentSegment = segments[segments.length - 1] ?? 'dashboard';
  const pageLabel = routeLabel(t, currentSegment);

  return (
    <div className="admin-dashboard-container">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="admin-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close menu"
        />
      )}

      {/* Sidebar */}
      <AdminSidebar
        onLogout={handleLogout}
        loading={loading}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        adminRole={adminRole}
      />

      {/* Main */}
      <div className="admin-dashboard-main">
        {/* Topbar */}
        <header className="admin-topnav">
          <button
            className="admin-menu-toggle"
            onClick={() => setSidebarOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="3" y1="6"  x2="21" y2="6"  />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>

          <div className="admin-breadcrumbs">
            <span>{t('common.admin')}</span>
            <span style={{ opacity: 0.4 }}>›</span>
            <span>{pageLabel}</span>
          </div>

          <div className="admin-topnav-right">
            <LanguageSwitcher />
            <AdminNotificationsDropdown adminRole={adminRole} />

            {/* User chip */}
            <div className="admin-user-chip">
              <div className="admin-user-avatar">
                {getInitials(user?.email)}
              </div>
              <div className="admin-user-info-text">
                <span className="admin-role-badge">{getRoleDisplayName(adminRole)}</span>
                <span className="admin-user-email">{user?.email}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page */}
        <main className="admin-page-content">
          <Outlet context={{ user, adminRole }} />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
